import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Copy, Gift, Loader2, ShieldCheck, UserPlus } from "lucide-react";
import AppStoreButtons from "../components/landing/AppStoreButtons";
import "../styles/landing.css";

const APP_API = "https://thryvin-production-fbdd.up.railway.app";

export default function Referral() {
  const { code: rawCode } = useParams();
  const code = String(rawCode || "").trim().toUpperCase();
  const [status, setStatus] = useState("loading");
  const [name, setName] = useState("");
  const [copied, setCopied] = useState(false);
  const [customerOffer, setCustomerOffer] = useState(null);

  useEffect(() => {
    if (!/^[A-Z0-9._-]{3,24}$/.test(code)) { setStatus("invalid"); return; }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${APP_API}/api/affiliate/validate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        });
        const payload = await response.json();
        if (!response.ok || !payload?.valid) throw new Error("invalid");
        if (cancelled) return;
        setName(payload.name || "a Thryvin' partner");
        setCustomerOffer(payload.customerOffer || null);
        setStatus("valid");
        const marker = `thryvin-referral-click:${code}`;
        if (!sessionStorage.getItem(marker)) {
          sessionStorage.setItem(marker, "1");
          fetch(`${APP_API}/api/affiliate/click`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
          }).catch(() => sessionStorage.removeItem(marker));
        }
      } catch { if (!cancelled) setStatus("invalid"); }
    })();
    return () => { cancelled = true; };
  }, [code]);

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch { /* the code is on screen, so it can still be typed */ }
  }

  return (
    <div className="landing-page referral-page">
      <main className="referral-card">
        {status === "loading" ? <><Loader2 className="spinner aff-ic" size={32} /><h1>Opening your invitation…</h1></> : status === "invalid" ? <><h1>This referral link isn&apos;t active</h1><p>Check the link with the person who sent it, or visit Thryvin&apos; directly.</p><Link className="aff-cta" to="/">Explore Thryvin&apos;</Link></> : <>
          <div className="referral-icon"><UserPlus size={30} /></div>
          <p className="aff-eyebrow">A personal Thryvin&apos; invitation</p>
          <h1>{name} thinks you&apos;ll love Thryvin&apos;.</h1>
          <p className="referral-lead">Build workouts around your goals, equipment, schedule, and progress—with an AI coach in your pocket.</p>
          {customerOffer && (
            <div className="referral-offer">
              <Gift size={20} />
              <div><strong>{customerOffer.days} days of Pro free</strong><span>Then the monthly app-store price unless you cancel. Apple or Google confirms the full terms before you start.</span></div>
            </div>
          )}
          <div className="referral-code-box"><span>Use this code when you sign up</span><button onClick={copyCode}><strong>{code}</strong>{copied ? <Check size={18} /> : <Copy size={18} />}</button></div>
          <ol className="referral-steps">
            <li><span>1</span>Download Thryvin&apos;</li>
            <li><span>2</span>Create your account</li>
            <li><span>3</span>Type <strong>{code}</strong> in the &ldquo;Promo or referral code&rdquo; box</li>
          </ol>
          <AppStoreButtons />
          <p className="referral-note"><ShieldCheck size={15} /> The code only works when you first create your account{customerOffer ? `, and that's what unlocks your ${customerOffer.days} days free` : ""}.</p>
          <Link to="/" className="referral-home-link">See what Thryvin&apos; can do</Link>
        </>}
      </main>
    </div>
  );
}
